import { Component, OnInit, ViewChild, inject, ChangeDetectorRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Table, TableModule } from 'primeng/table';
import { ButtonModule } from 'primeng/button';
import { InputTextModule } from 'primeng/inputtext';
import { DialogModule } from 'primeng/dialog';
import { ToastModule } from 'primeng/toast';
import { FormsModule, ReactiveFormsModule, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Select } from 'primeng/select';
import { MessageService } from 'primeng/api';
import { PaymentDefinitionsService } from '@services/payment-definitions.service';
import { IPspErrorCodeType, ITypeListItem } from '@shared/interfaces/payment-definitions.interface';
import { ColumnFilterComponent } from '@app/shared/components/column-filter/column-filter.component';
import { IconField } from 'primeng/iconfield';
import { InputIcon } from 'primeng/inputicon';
import { PageHeaderComponent } from '@app/shared/components/page-header/page-header.component';

@Component({
    selector: 'app-psp-error-code',
    imports: [
        CommonModule,
        TableModule,
        ButtonModule,
        InputTextModule,
        DialogModule,
        ToastModule,
        FormsModule,
        ReactiveFormsModule,
        Select,
        ColumnFilterComponent,
        IconField,
        InputIcon,
        PageHeaderComponent
    ],
    templateUrl: './psp-error-code.component.html',
    styleUrl: './psp-error-code.component.scss'
})
export class PspErrorCodeComponent implements OnInit {
    @ViewChild('dt') dt!: Table;

    errorCodes: IPspErrorCodeType[] = [];
    pspTypes: ITypeListItem[] = [];
    loading = true;
    dialogVisible = false;
    isEditMode = false;
    selectedErrorCode: IPspErrorCodeType | null = null;
    submitted = false;
    errorCodeForm!: FormGroup;

    private paymentService = inject(PaymentDefinitionsService);
    private fb = inject(FormBuilder);
    private messageService = inject(MessageService);
    private cd = inject(ChangeDetectorRef);

    ngOnInit(): void {
        this.initializeForm();
        this.loadErrorCodes();
        this.loadDropdowns();
    }

    onGlobalSearch(event: Event): void {
        const value = (event.target as HTMLInputElement).value;
        this.dt.filterGlobal(value, 'contains');
    }

    loadErrorCodes(): void {
        this.loading = true;
        this.cd.detectChanges();
        this.paymentService.getErrorCodes().subscribe({
            next: (res) => {
                this.errorCodes = res.pspErrorCodeTypes || [];
                this.loading = false;
                this.cd.detectChanges();
            },
            error: () => {
                this.loading = false;
                this.cd.detectChanges();
            }
        });
    }

    loadDropdowns(): void {
        this.paymentService.getPspTypeInformation().subscribe({
            next: (res) => {
                this.pspTypes = res.typeList || [];
            }
        });
    }

    initializeForm(): void {
        this.errorCodeForm = this.fb.group({
            code: ['', [Validators.required]],
            error: ['', [Validators.required]],
            message: ['', [Validators.required]],
            pspTypeId: [null, [Validators.required]]
        });
    }

    isInvalid(field: string): boolean {
        const control = this.errorCodeForm.get(field);
        return !!(control && control.invalid && (this.submitted || control.dirty || control.touched));
    }

    openCreate(): void {
        this.isEditMode = false;
        this.selectedErrorCode = null;
        this.submitted = false;
        this.errorCodeForm.reset({ code: '', error: '', message: '', pspTypeId: null });
        this.dialogVisible = true;
    }

    openEdit(errorCode: IPspErrorCodeType): void {
        this.isEditMode = true;
        this.selectedErrorCode = errorCode;
        this.submitted = false;
        this.errorCodeForm.patchValue({
            code: errorCode.code,
            error: errorCode.error,
            message: errorCode.message,
            pspTypeId: errorCode.pspTypeId
        });
        this.dialogVisible = true;
    }

    cancelDialog(): void {
        this.dialogVisible = false;
    }

    saveErrorCode(): void {
        this.submitted = true;
        if (this.errorCodeForm.invalid) {
            return;
        }

        const formData = this.errorCodeForm.value;
        const request = {
            code: formData.code,
            error: formData.error,
            message: formData.message,
            pspTypeId: formData.pspTypeId
        };

        if (!this.isEditMode) {
            this.paymentService.createErrorCode(request).subscribe({
                next: () => {
                    this.messageService.add({
                        severity: 'success',
                        summary: 'Success',
                        detail: 'Error code has been created successfully.'
                    });
                    this.loadErrorCodes();
                    this.dialogVisible = false;
                }
            });
        } else if (this.selectedErrorCode) {
            this.paymentService.updateErrorCode(this.selectedErrorCode.id, request).subscribe({
                next: () => {
                    this.messageService.add({
                        severity: 'success',
                        summary: 'Success',
                        detail: 'Error code has been updated successfully.'
                    });
                    this.loadErrorCodes();
                    this.dialogVisible = false;
                }
            });
        }
    }
}
